import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiComponents } from '@aztec-realms/api-client';
import { PrimaryActionButton, StonePanel } from '@aztec-realms/ui';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  confirmPlayerPasswordReset,
  getRegistrationPolicy,
  registerPlayer,
  requestPlayerPasswordReset,
  requestPlayerVerification,
  verifyPlayerEmail,
} from './data/player-auth';
import {
  forgetInitialRegistrationInvitation,
  initialRegistrationInvitation,
  invitationFromFragment,
} from './data/registration-invite';
import { BrowserApiError } from './data/browser-api';

const PENDING_ACCOUNT_EMAIL_KEY = 'aztec-realms:pending-account-email';
const PUBLIC_ACCOUNT_PATHS = new Set([
  '/register',
  '/verify-email',
  '/resend-verification',
  '/forgot-password',
  '/reset-password',
]);

type AccountFieldIcon = 'email' | 'identity' | 'lock';

interface AccountShellProps {
  children: ReactNode;
  eyebrow: string;
  intro: string;
  state: string;
  title: string;
}

interface AccountFieldProps {
  autoComplete: string;
  disabled?: boolean;
  icon: AccountFieldIcon;
  label: string;
  maxLength?: number;
  minLength?: number;
  onChange: (value: string) => void;
  placeholder: string;
  type?: 'email' | 'text';
  value: string;
}

interface PasswordFieldProps {
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  value: string;
}

export type RegistrationInput = ApiComponents['schemas']['RegisterRequest'];

interface PlayerRegistrationScreenProps {
  register?: typeof registerPlayer;
  resend?: typeof requestPlayerVerification;
  policy?: typeof getRegistrationPolicy;
}

interface RequestEmailScreenProps {
  action: typeof requestPlayerPasswordReset | typeof requestPlayerVerification;
  eyebrow: string;
  intro: string;
  kind: 'password' | 'verification';
  submitLabel: string;
  title: string;
}

interface VerifyEmailScreenProps {
  verify?: typeof verifyPlayerEmail;
}

interface ResetPasswordScreenProps {
  confirm?: typeof confirmPlayerPasswordReset;
}

function browserLocale(): string {
  if (typeof navigator === 'undefined') return 'en';
  const candidate = navigator.language.match(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?/u)?.[0];
  return candidate ?? 'en';
}

function browserTimezone(): string {
  if (typeof Intl === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function rememberPendingEmail(email: string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PENDING_ACCOUNT_EMAIL_KEY, email);
}

export function pendingAccountEmail(): string {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(PENDING_ACCOUNT_EMAIL_KEY) ?? '';
}

export function clearPendingAccountEmail(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_ACCOUNT_EMAIL_KEY);
}

export function isPublicAccountPath(pathname: string): boolean {
  return PUBLIC_ACCOUNT_PATHS.has(pathname);
}

function AccountStory() {
  return (
    <section aria-label="Aztec Realms" className="player-login-story">
      <div className="player-login-brand">
        <span aria-hidden="true" className="player-login-brand__mark">
          <svg focusable="false" viewBox="0 0 64 64">
            <circle cx="32" cy="17" r="8" />
            <path d="M32 3v5M20 7l5 6m19-6-5 6M14 17h7m22 0h7" />
            <path d="M12 55h40M18 54V43h28v11M23 43V32h18v11M28 32V22h8v10" />
            <path d="m23 54 9-12 9 12" />
          </svg>
        </span>
        <span>
          <strong>Aztec Realms</strong>
          <small>Runes &amp; Tribes</small>
        </span>
      </div>
      <div className="player-login-story__copy">
        <span className="player-login-eyebrow">Begin with a name, grow through action</span>
        <h2>Your first path starts here.</h2>
        <p>
          Create one secure identity for your discoveries, restored knowledge and living settlement.
        </p>
        <ol aria-label="Core game loop" className="player-login-loop">
          {['Explore', 'Decode', 'Build', 'Belong'].map((step, index) => (
            <li key={step}>
              <span aria-hidden="true">0{index + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </div>
      <span className="player-login-world-status">
        <span aria-hidden="true" /> Account protection active
      </span>
    </section>
  );
}

function AccountShell({ children, eyebrow, intro, state, title }: AccountShellProps) {
  return (
    <main
      aria-labelledby={`${state}-title`}
      className="player-login-shell player-account-shell"
      data-visual-ready="true"
      data-visual-state={state}
    >
      <div aria-hidden="true" className="player-login-atmosphere">
        <span className="player-login-atmosphere__sun" />
        <span className="player-login-atmosphere__water" />
      </div>
      <AccountStory />
      <StonePanel className="player-login-card player-account-card" ornament={2}>
        <a className="account-back-link" href="/">
          <span aria-hidden="true">←</span> Back to sign in
        </a>
        <header className="player-login-card__header">
          <div aria-hidden="true" className="player-login-seal">
            <span />
          </div>
          <div>
            <span className="player-login-eyebrow">{eyebrow}</span>
            <h1 id={`${state}-title`}>{title}</h1>
          </div>
        </header>
        <p className="player-login-intro">{intro}</p>
        {children}
        <div className="player-login-security">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
            <path d="M12 3 5 6v5c0 4.4 2.8 8.3 7 10 4.2-1.7 7-5.6 7-10V6l-7-3Z" />
            <path d="m9 12 2 2 4-5" />
          </svg>
          <p>
            <strong>Private by design</strong>
            Passwords are hashed · location permission remains separate
          </p>
        </div>
      </StonePanel>
    </main>
  );
}

function FieldIcon({ icon }: { icon: AccountFieldIcon }) {
  if (icon === 'identity') {
    return (
      <svg focusable="false" viewBox="0 0 24 24">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c.6-4 3-6 7-6s6.4 2 7 6" />
      </svg>
    );
  }
  if (icon === 'lock') {
    return (
      <svg focusable="false" viewBox="0 0 24 24">
        <rect height="10" rx="2" width="15" x="4.5" y="10" />
        <path d="M8 10V7.5a4 4 0 0 1 8 0V10M12 14v2" />
      </svg>
    );
  }
  return (
    <svg focusable="false" viewBox="0 0 24 24">
      <path d="M3.5 6.5h17v12h-17zM4 7l8 6 8-6" />
    </svg>
  );
}

function AccountField({
  autoComplete,
  disabled,
  icon,
  label,
  maxLength,
  minLength,
  onChange,
  placeholder,
  type = 'text',
  value,
}: AccountFieldProps) {
  const id = useId();
  return (
    <div className="player-login-field">
      <label htmlFor={id}>{label}</label>
      <div className="player-login-input">
        <span aria-hidden="true" className="player-login-input__icon">
          <FieldIcon icon={icon} />
        </span>
        <input
          autoCapitalize={type === 'email' ? 'none' : 'words'}
          autoComplete={autoComplete}
          disabled={disabled}
          id={id}
          maxLength={maxLength}
          minLength={minLength}
          onChange={(event) => onChange(event.currentTarget.value)}
          placeholder={placeholder}
          required
          spellCheck={type !== 'email'}
          type={type}
          value={value}
        />
      </div>
    </div>
  );
}

function PasswordField({ disabled, label, onChange, value }: PasswordFieldProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <div className="player-login-field">
      <label htmlFor={id}>{label}</label>
      <div className="player-login-input">
        <span aria-hidden="true" className="player-login-input__icon">
          <FieldIcon icon="lock" />
        </span>
        <input
          autoComplete="new-password"
          disabled={disabled}
          id={id}
          maxLength={128}
          minLength={12}
          onChange={(event) => onChange(event.currentTarget.value)}
          placeholder="At least 12 characters"
          required
          type={visible ? 'text' : 'password'}
          value={value}
        />
        <button
          aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLocaleLowerCase()}`}
          aria-pressed={visible}
          className="player-login-password-toggle"
          disabled={disabled}
          onClick={() => setVisible((current) => !current)}
          type="button"
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

function AccountError({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <div className="player-login-error" role="alert">
      <span aria-hidden="true">!</span>
      <p>
        <strong>The path did not open.</strong>
        {error instanceof Error ? error.message : fallback}
      </p>
    </div>
  );
}

function AccountSuccess({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section aria-live="polite" className="account-success" role="status">
      <span aria-hidden="true" className="account-success__seal">
        ✓
      </span>
      <div>
        <strong>{title}</strong>
        {children}
      </div>
    </section>
  );
}

function registrationFailureMessage(error: unknown): string {
  if (error instanceof BrowserApiError) {
    if (error.code === 'invitation_unavailable') {
      return 'This invitation could not be used. Check the code and invited email address, or ask for a new invitation.';
    }
    if (
      ['password_too_common', 'password_context_word', 'password_breached'].includes(error.code)
    ) {
      return 'Choose a less predictable password that has not appeared in a known breach. Avoid common or game-related words.';
    }
  }
  return 'Your account could not be created. Check your details or try again later.';
}

export function PlayerRegistrationScreen({
  register = registerPlayer,
  resend = requestPlayerVerification,
  policy = getRegistrationPolicy,
}: PlayerRegistrationScreenProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState(
    () => invitationFromFragment(location.hash) || initialRegistrationInvitation(),
  );
  const [invitationSource, setInvitationSource] = useState(location.hash);
  if (location.hash !== invitationSource) {
    // Synchronize a replacement email link on the already-mounted route before
    // its fragment is cleared. Clearing the URL must not clear the captured code.
    setInvitationSource(location.hash);
    if (location.hash) setInviteCode(invitationFromFragment(location.hash));
  }
  useEffect(() => {
    forgetInitialRegistrationInvitation();
    if (location.hash) navigate(location.pathname + location.search, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);
  const registrationPolicy = useQuery({
    queryKey: ['public', 'registration-policy'],
    queryFn: policy,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const inviteRequired = registrationPolicy.data?.mode !== 'open';
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [localError, setLocalError] = useState('');
  const clearAccountSecrets = () => {
    rememberPendingEmail(email.trim());
    setInviteCode('');
    setPassword('');
    setConfirmation('');
  };
  const registration = useMutation({
    gcTime: 0,
    retry: false,
    mutationFn: () =>
      register({
        display_name: displayName.trim(),
        email: email.trim(),
        invite_code: inviteCode.trim() || undefined,
        locale: browserLocale(),
        password,
        timezone: browserTimezone(),
      }),
    onSuccess: clearAccountSecrets,
    onError: (error) => {
      if (
        error instanceof BrowserApiError &&
        error.code === 'registration_verification_delivery_unavailable'
      ) {
        clearAccountSecrets();
      }
    },
  });
  const verificationDeliveryFailed =
    registration.error instanceof BrowserApiError &&
    registration.error.code === 'registration_verification_delivery_unavailable';
  const verification = useMutation({ mutationFn: () => resend(email.trim()) });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError('');
    registration.reset();
    if (!registrationPolicy.data || registrationPolicy.isError) {
      setLocalError('Check registration availability before creating your account.');
      return;
    }
    if (inviteRequired && !inviteCode.trim()) {
      setLocalError('Enter the invitation code from your email to join.');
      return;
    }
    if (password !== confirmation) {
      setLocalError('The two passwords do not match.');
      return;
    }
    registration.mutate();
  };

  return (
    <AccountShell
      eyebrow="Create your wayfinder"
      intro="Your account keeps every discovery, Decode and settlement change connected across devices."
      state="player-registration"
      title="Begin a new path"
    >
      {registration.isSuccess || verificationDeliveryFailed ? (
        <div className="account-completion">
          <AccountSuccess
            title={
              verificationDeliveryFailed
                ? 'Account created · verification needed'
                : 'Check your email'
            }
          >
            {verificationDeliveryFailed ? (
              <p>
                Your account was created, but its verification email could not be sent. Your
                invitation has been used; request a fresh verification email below instead of
                registering again.
              </p>
            ) : (
              <p>
                We sent a single-use verification path to <strong>{email.trim()}</strong>. Verify it
                before signing in.
              </p>
            )}
          </AccountSuccess>
          {verification.isSuccess && (
            <div className="player-login-notice" role="status">
              <span aria-hidden="true">✓</span>
              <p>If the address is eligible, a fresh verification email has been sent.</p>
            </div>
          )}
          {verification.error && (
            <AccountError
              error={verification.error}
              fallback="A verification message could not be requested."
            />
          )}
          <button
            className="account-secondary-action"
            disabled={verification.isPending}
            onClick={() => verification.mutate()}
            type="button"
          >
            {verification.isPending ? 'Requesting…' : 'Send verification email again'}
          </button>
          <a className="account-primary-link" href="/">
            Continue to sign in
          </a>
        </div>
      ) : (
        <form className="player-login-form" onSubmit={submit}>
          {registrationPolicy.isPending ? (
            <p role="status">Checking registration availability…</p>
          ) : registrationPolicy.isError ? (
            <div className="account-completion">
              <AccountError
                error={undefined}
                fallback="Registration availability could not be checked. Please try again."
              />
              <button
                className="account-secondary-action"
                onClick={() => void registrationPolicy.refetch()}
                type="button"
              >
                Check registration again
              </button>
            </div>
          ) : inviteRequired ? (
            <p className="account-privacy-note">
              Access is by invitation. Use the code and email address from your invitation. An
              invitation opens a player account, never administrator access.
            </p>
          ) : null}
          <div className="player-login-fields account-fields">
            {(inviteRequired || inviteCode) && (
              <div className="player-login-field">
                <label htmlFor="registration-invite">Invitation code</label>
                <div className="player-login-input">
                  <span aria-hidden="true" className="player-login-input__icon">
                    <FieldIcon icon="lock" />
                  </span>
                  <input
                    autoCapitalize="none"
                    autoComplete="off"
                    disabled={registration.isPending}
                    id="registration-invite"
                    maxLength={256}
                    onChange={(event) => {
                      setInviteCode(event.currentTarget.value);
                      setLocalError('');
                    }}
                    placeholder="Code from your invitation email"
                    required={inviteRequired}
                    spellCheck={false}
                    type="password"
                    value={inviteCode}
                  />
                </div>
              </div>
            )}
            <AccountField
              autoComplete="name"
              disabled={registration.isPending}
              icon="identity"
              label="Player name"
              maxLength={64}
              minLength={2}
              onChange={(value) => {
                setDisplayName(value);
                setLocalError('');
              }}
              placeholder="How other players see you"
              value={displayName}
            />
            <AccountField
              autoComplete="email"
              disabled={registration.isPending}
              icon="email"
              label="Email address"
              onChange={(value) => {
                setEmail(value);
                setLocalError('');
              }}
              placeholder="wayfinder@example.com"
              type="email"
              value={email}
            />
            <PasswordField
              disabled={registration.isPending}
              label="Create password"
              onChange={(value) => {
                setPassword(value);
                setLocalError('');
              }}
              value={password}
            />
            <PasswordField
              disabled={registration.isPending}
              label="Confirm password"
              onChange={(value) => {
                setConfirmation(value);
                setLocalError('');
              }}
              value={confirmation}
            />
          </div>
          <div className="account-password-guide">
            <strong>Make it yours</strong>
            <span>Use 12–128 characters and avoid common passwords or game-related words.</span>
            <span>Known breached passwords are rejected securely by the server.</span>
          </div>
          {localError && <AccountError error={new Error(localError)} fallback={localError} />}
          {registration.error && (
            <AccountError
              error={undefined}
              fallback={registrationFailureMessage(registration.error)}
            />
          )}
          <PrimaryActionButton
            disabled={!registrationPolicy.data || registrationPolicy.isError}
            loading={registration.isPending}
            type="submit"
          >
            {registration.isPending ? 'Creating your path…' : 'Create account'}
          </PrimaryActionButton>
          <p className="account-privacy-note">
            Account email is used for security and essential account messages. Location access is
            requested separately when you choose Explore.
          </p>
        </form>
      )}
    </AccountShell>
  );
}

function RequestEmailScreen({
  action,
  eyebrow,
  intro,
  kind,
  submitLabel,
  title,
}: RequestEmailScreenProps) {
  const [email, setEmail] = useState(pendingAccountEmail());
  const request = useMutation({ mutationFn: () => action(email.trim()) });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    request.reset();
    rememberPendingEmail(email.trim());
    request.mutate();
  };
  return (
    <AccountShell eyebrow={eyebrow} intro={intro} state={`player-${kind}-request`} title={title}>
      {request.isSuccess ? (
        <div className="account-completion">
          <AccountSuccess title="Check your email">
            <p>
              If this address is eligible, its single-use{' '}
              {kind === 'password' ? 'reset' : 'verification'} path is now on the way.
            </p>
          </AccountSuccess>
          <a className="account-primary-link" href="/">
            Return to sign in
          </a>
        </div>
      ) : (
        <form className="player-login-form" onSubmit={submit}>
          <div className="player-login-fields">
            <AccountField
              autoComplete="email"
              disabled={request.isPending}
              icon="email"
              label="Email address"
              onChange={(value) => {
                setEmail(value);
                request.reset();
              }}
              placeholder="wayfinder@example.com"
              type="email"
              value={email}
            />
          </div>
          {request.error && (
            <AccountError error={request.error} fallback="The message could not be requested." />
          )}
          <PrimaryActionButton loading={request.isPending} type="submit">
            {request.isPending ? 'Requesting secure link…' : submitLabel}
          </PrimaryActionButton>
          <p className="account-privacy-note">
            The same confirmation appears whether or not an account exists for this address.
          </p>
        </form>
      )}
    </AccountShell>
  );
}

export function RequestVerificationScreen() {
  return (
    <RequestEmailScreen
      action={requestPlayerVerification}
      eyebrow="Verify your identity"
      intro="Request a fresh single-use link if the first verification message expired or never arrived."
      kind="verification"
      submitLabel="Send verification email"
      title="Open your account path"
    />
  );
}

export function ForgotPasswordScreen() {
  return (
    <RequestEmailScreen
      action={requestPlayerPasswordReset}
      eyebrow="Recover your path"
      intro="Request a single-use reset link. Your existing sessions are revoked when the password changes."
      kind="password"
      submitLabel="Send password reset"
      title="Restore account access"
    />
  );
}

export function VerifyEmailScreen({ verify = verifyPlayerEmail }: VerifyEmailScreenProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const started = useRef(false);
  const parameters = new URLSearchParams(location.search);
  const complete = parameters.get('complete') === '1';
  const token = parameters.get('token')?.trim() ?? '';
  const verification = useMutation({
    mutationFn: () => verify(token),
    onSuccess: () => navigate('/verify-email?complete=1', { replace: true }),
  });
  const { mutate } = verification;

  useEffect(() => {
    if (complete || started.current || token.length < 32) return;
    started.current = true;
    mutate();
  }, [complete, mutate, token]);

  const invalidLink = !complete && token.length < 32;
  return (
    <AccountShell
      eyebrow="Email verification"
      intro="This single-use link binds the address to the account before the first secure session begins."
      state="player-email-verification"
      title={complete || verification.isSuccess ? 'Identity confirmed' : 'Checking your path'}
    >
      {complete || verification.isSuccess ? (
        <div className="account-completion">
          <AccountSuccess title="Email verified">
            <p>Your account can now enter the realm from any supported device.</p>
          </AccountSuccess>
          <a className="account-primary-link" href="/?verified=1">
            Continue to sign in
          </a>
        </div>
      ) : verification.isPending ? (
        <div aria-live="polite" className="account-verifying" role="status">
          <span aria-hidden="true" />
          Verifying the single-use path…
        </div>
      ) : invalidLink || verification.error ? (
        <div className="account-completion">
          <AccountError
            error={verification.error}
            fallback="This verification link is missing, invalid or expired."
          />
          <a className="account-secondary-action" href="/resend-verification">
            Request another verification email
          </a>
        </div>
      ) : null}
    </AccountShell>
  );
}

export function ResetPasswordScreen({
  confirm = confirmPlayerPasswordReset,
}: ResetPasswordScreenProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const parameters = new URLSearchParams(location.search);
  const complete = parameters.get('complete') === '1';
  const token = parameters.get('token')?.trim() ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [localError, setLocalError] = useState('');
  const reset = useMutation({
    mutationFn: () => confirm(token, password),
    onSuccess: () => navigate('/reset-password?complete=1', { replace: true }),
  });
  const invalidLink = !complete && token.length < 32;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError('');
    reset.reset();
    if (password !== confirmation) {
      setLocalError('The two passwords do not match.');
      return;
    }
    reset.mutate();
  };

  return (
    <AccountShell
      eyebrow="Secure password reset"
      intro="Choose a new password. Completing this step revokes every existing account session."
      state="player-password-reset"
      title={complete || reset.isSuccess ? 'Access restored' : 'Create a new key'}
    >
      {complete || reset.isSuccess ? (
        <div className="account-completion">
          <AccountSuccess title="Password changed">
            <p>All previous sessions were revoked. Sign in again with your new password.</p>
          </AccountSuccess>
          <a className="account-primary-link" href="/?password-reset=1">
            Sign in securely
          </a>
        </div>
      ) : invalidLink ? (
        <div className="account-completion">
          <AccountError
            error={undefined}
            fallback="This reset link is missing, invalid or expired."
          />
          <a className="account-secondary-action" href="/forgot-password">
            Request another reset email
          </a>
        </div>
      ) : (
        <form className="player-login-form" onSubmit={submit}>
          <div className="player-login-fields">
            <PasswordField
              disabled={reset.isPending}
              label="New password"
              onChange={(value) => {
                setPassword(value);
                setLocalError('');
              }}
              value={password}
            />
            <PasswordField
              disabled={reset.isPending}
              label="Confirm new password"
              onChange={(value) => {
                setConfirmation(value);
                setLocalError('');
              }}
              value={confirmation}
            />
          </div>
          <div className="account-password-guide">
            <strong>Protect every device</strong>
            <span>Use 12–128 characters and a password you do not use elsewhere.</span>
          </div>
          {localError && <AccountError error={new Error(localError)} fallback={localError} />}
          {reset.error && (
            <AccountError error={reset.error} fallback="Your password could not be reset." />
          )}
          <PrimaryActionButton loading={reset.isPending} type="submit">
            {reset.isPending ? 'Replacing your key…' : 'Set new password'}
          </PrimaryActionButton>
        </form>
      )}
    </AccountShell>
  );
}

export function PublicAccountScreen() {
  const { pathname } = useLocation();
  if (pathname === '/register') return <PlayerRegistrationScreen />;
  if (pathname === '/verify-email') return <VerifyEmailScreen />;
  if (pathname === '/resend-verification') return <RequestVerificationScreen />;
  if (pathname === '/forgot-password') return <ForgotPasswordScreen />;
  return <ResetPasswordScreen />;
}
